import {
  Checkbox,
  Col,
  Form,
  FormInstance,
  Radio,
  Row,
  Space,
  Typography,
} from 'antd';
import { SituationOptions } from '@/core/domain/reports/movements/workOrders/WorkOrderReport.types';
import {
  CheckboxGroupButtons,
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import { DateFilterByTypes } from '@/core/domain/NetReport/WorkOrder/WorkOrder.types';
import { ReportCustomersProspectingForm } from '@/core/domain/reports/people/Report.types';
import { useWindowSize } from '@/hooks';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import useDefaultMinNumberFieldError from '@/hooks/useDefaultMinNumberFieldError';
import { WorkOrderType } from '@/core/domain/reports/movements/abcCurve/abcCurve.types';
import { isAppPortocar } from '@/index';

interface Props {
  form: FormInstance<ReportCustomersProspectingForm>;
}

const ProspectingForm = ({ form }: Props) => {
  const { Title } = Typography;
  const { isMobile } = useWindowSize();

  const [monthSelected, setMonthSelected] = useState<string>();

  const startMonth = moment(monthSelected).startOf('month');
  const endMonth = moment(monthSelected).endOf('month');

  const initialDate = Form.useWatch('startDate', form);
  const finalDate = Form.useWatch('endDate', form);

  const initialOsKm = Form.useWatch('initialOsKm', form);
  const initialOilChangeKm = Form.useWatch('initialOilChangeKm', form);

  const defaultMinNumberFieldError = useDefaultMinNumberFieldError();

  useEffect(() => {
    form.setFieldsValue({
      startDate: startMonth,
      endDate: endMonth,
    });
  }, [monthSelected]);

  const situationOptions = [
    { label: 'Abertos', value: SituationOptions.open },
    { label: 'Autorizados', value: SituationOptions.authorized },
    { label: 'Fechados', value: SituationOptions.closed },
    { label: 'Cancelados', value: SituationOptions.canceled },
  ];

  const osTypeOptions = [
    {
      label: 'Orçamento',
      value: WorkOrderType.WORK_ORDER,
    },
    {
      label: 'Garantia',
      value: WorkOrderType.WARRANTY,
    },
    {
      label: 'Retorno',
      value: WorkOrderType.RETURN,
    },
    {
      label: isAppPortocar ? 'Interna (Cortesia)' : 'Cortesia',
      value: WorkOrderType.COURTESY,
    },
    {
      label: 'Perda Total',
      value: WorkOrderType.TOTAL_LOSS,
    },
    {
      label: 'Revisão',
      value: WorkOrderType.REVISION,
    },
  ];

  const months = new Array(6)
    .fill('')
    .map((_, index) => moment().subtract(index + 1, 'months'))
    .reverse();

  function validationDates(value: Moment, isFinalDate: boolean) {
    if (!value) return Promise.resolve();

    if (isFinalDate && initialDate && value.isBefore(initialDate)) {
      return Promise.reject(
        new Error(
          `Deve ser maior ou igual a ${moment(initialDate).format(
            'DD/MM/YYYY',
          )}`,
        ),
      );
    }

    if (!isFinalDate && finalDate && value.isAfter(finalDate)) {
      return Promise.reject(
        new Error(
          `Deve ser menor ou igual a ${moment(finalDate).format('DD/MM/YYYY')}`,
        ),
      );
    }

    return Promise.resolve();
  }

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5}>Selecione o mês desejado</Title>

          <Row gutter={16}>
            <Col span={24} md={12}>
              <FloatingFormItem
                label="Data inicial do filtro"
                name="startDate"
                rules={[
                  { required: true, message: 'Informe a data inicial' },
                  {
                    validator: (_, value) => validationDates(value, false),
                  },
                ]}
              >
                <InputDate
                  disabledDate={(date) =>
                    !!finalDate && date.isAfter(finalDate)
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                label="Data final do filtro"
                name="endDate"
                dependencies={['startDate']}
                rules={[
                  { required: true, message: 'Informe a data final' },
                  {
                    validator: (_, value) => validationDates(value, true),
                  },
                ]}
              >
                <InputDate
                  disabledDate={(date) =>
                    !!initialDate && date.isBefore(initialDate)
                  }
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Title level={5}>Ou marque o mês desejado</Title>

          <Col span={24}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              size="large"
              onChange={({ target }) => {
                setMonthSelected(target.value);

                form.setFieldsValue({
                  startDate: moment(target.value).startOf('month'),
                  endDate: moment(target.value).endOf('month'),
                });
              }}
            >
              {months.map((month) => (
                <Radio
                  key={month.toISOString()}
                  value={month.format('YYYY-MM-DD')}
                  style={{ width: isMobile ? '50%' : '' }}
                >
                  {month.format('MMM/YYYY').toUpperCase()}
                </Radio>
              ))}
            </Radio.Group>
          </Col>
        </Col>

        <Col sm={24} md={12}>
          <Title level={5}>Situação</Title>

          <Form.Item
            name="situation"
            initialValue={[SituationOptions.open]}
            noStyle
          >
            <CheckboxGroupButtons options={situationOptions} />
          </Form.Item>
        </Col>

        <Col span={24} md={12}>
          <Title level={5}>Filtrar por data de</Title>

          <Space align="center">
            <FloatingFormItem
              name="dateFilterBy"
              initialValue={DateFilterByTypes.entry}
              noStyle
            >
              <Radio.Group optionType="button" buttonStyle="solid" size="large">
                <Radio value={DateFilterByTypes.entry}>Entrada</Radio>
                <Radio value={DateFilterByTypes.authorization}>
                  Autorização
                </Radio>
                <Radio value={DateFilterByTypes.closing}>Fechamento</Radio>
                <Radio value={DateFilterByTypes.leave}>Saída</Radio>
              </Radio.Group>
            </FloatingFormItem>
          </Space>
        </Col>

        <Col xs={24}>
          <Title level={5}>Tipo de orçamento</Title>

          <FloatingFormItem
            name="osType"
            initialValue={[WorkOrderType.WORK_ORDER]}
          >
            <CheckboxGroupButtons options={osTypeOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="companiesIds" label="Empresas">
            <CompaniesInfiniteSelectField
              allowClear
              mode="multiple"
              queryFilter={{ rows: 50 }}
              queryEnable
              selectAll
              activityFilter
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem name="customersIds" label="Clientes">
            <CustomersSelectField mode="multiple" />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Title level={5}>Campos de KM</Title>

          <Row gutter={16}>
            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label="KM inicial do orçamento"
                name="initialOsKm"
              >
                <InputDecimal fractions={0} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label="KM final do orçamento"
                name="finalOsKm"
                rules={[
                  defaultMinNumberFieldError(
                    initialOsKm,
                    `O KM deve ser maior que o KM inicial do orçamento: ${initialOsKm}`,
                  ),
                ]}
              >
                <InputDecimal fractions={0} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label="KM inicial da troca de óleo"
                name="initialOilChangeKm"
              >
                <InputDecimal fractions={0} />
              </FloatingFormItem>
            </Col>

            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label="KM final da troca de óleo"
                name="finalOilChangeKm"
                rules={[
                  defaultMinNumberFieldError(
                    initialOilChangeKm,
                    `O KM deve ser maior que o KM final da troca de óleo: ${initialOilChangeKm}`,
                  ),
                ]}
              >
                <InputDecimal fractions={0} />
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24}>
          <FloatingFormItem
            label="Descrição do serviço"
            name="serviceDescription"
          >
            <InputText />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Form.Item
            name="thirdParty"
            initialValue={false}
            valuePropName="checked"
          >
            <Checkbox>Serviço de terceiros</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default ProspectingForm;
